import { useCallback, useEffect, useRef, useState } from 'react'

import type { AIFieldState } from '@/hooks/types/aiFieldGeneration'

/**
 * Badge state between saves. The prop is the server's answer and only moves when the
 * form is saved and refetched, so typing has to hide the badge locally or it keeps
 * crediting the machine for text the expert just replaced. Accepting a suggestion sets
 * it the other way, and the override is dropped whenever the server's answer changes.
 */
export function useGeneratedBadge(
  isGenerated: boolean | undefined,
  fieldState: AIFieldState,
  onAccept: () => void,
  onReject: () => void,
) {
  const [override, setOverride] = useState<boolean | null>(null)

  const [previousIsGenerated, setPreviousIsGenerated] = useState(isGenerated)
  if (isGenerated !== previousIsGenerated) {
    setPreviousIsGenerated(isGenerated)
    setOverride(null)
  }

  // A generation that lands makes the value the machine's again however it was accepted —
  // the auto-accept path never calls `handleAccept`. Keyed on it landing rather than on it
  // starting, so a cancelled or failed one leaves the badge exactly as it was: the field
  // still holds whatever it held before, and claiming that for the Sidekick is the wrong
  // way to be wrong.
  //
  // The whole transition is tracked, not just the outcome. The outcome is set while the
  // field is still `pending` and stays set afterwards, so comparing outcomes alone both
  // misses the second generation of a field and re-badges an edited one on remount.
  const previousRef = useRef({ state: fieldState.state, outcome: fieldState.lastOutcome })
  useEffect(() => {
    const previous = previousRef.current
    previousRef.current = { state: fieldState.state, outcome: fieldState.lastOutcome }

    if (fieldState.state !== 'idle' || fieldState.lastOutcome !== 'succeeded') return
    // Came to rest here, rather than already having been at rest when this mounted.
    if (previous.state !== 'idle' || previous.outcome !== 'succeeded') setOverride(true)
  }, [fieldState.state, fieldState.lastOutcome])

  return {
    showBadge: override ?? !!isGenerated,
    markEdited: useCallback(() => setOverride(false), []),
    handleAccept: useCallback(() => {
      setOverride(true)
      onAccept()
    }, [onAccept]),
    handleReject: useCallback(() => {
      setOverride(null)
      onReject()
    }, [onReject]),
  }
}

export const GENERATION_FAILED_MESSAGE = "Sidekick couldn't write this one. Use Ask Sidekick to try again."

/**
 * A failed generation has nowhere else to appear.
 *
 * It used to be reported by the helper line under "Done Adding Files", which SME-1135
 * removed — so without this a pass that fails leaves a required field silently blank and
 * the expert meets it as an error at save, which is the experience that ticket exists to
 * end.
 *
 * Self-clearing: `lastOutcome` is cleared when the next request starts and set to
 * `succeeded` when one lands, so a retry takes the message with it. Typing dismisses it
 * too, since the expert writing the field themselves is the other way the problem goes.
 */
export function useGenerationError(fieldState: AIFieldState, isEmpty: boolean) {
  // Only when the field is actually empty. A pass the expert never asked for regenerates
  // fields that already hold good text (decision 46 refreshes anything still the machine's),
  // and a failure there leaves nothing wrong on screen — marking it red, and forcing Advanced
  // open around it, reports a problem the expert cannot see and does not have.
  const failed = fieldState.state === 'idle' && fieldState.lastOutcome === 'failed' && isEmpty
  const [dismissed, setDismissed] = useState(false)

  const [previousFailed, setPreviousFailed] = useState(failed)
  if (failed !== previousFailed) {
    setPreviousFailed(failed)
    if (!failed) setDismissed(false)
  }

  return {
    message: failed && !dismissed ? GENERATION_FAILED_MESSAGE : null,
    dismiss: useCallback(() => setDismissed(true), []),
  }
}
